/** Data hooks for the chat page. */

import { useMutation, useQueries, useQuery } from '@tanstack/react-query'
import { api } from '@/core/api/base.api.client'
import type { ChatAnswer, ChatAttachment, ChatModels, ChatOptions, ChatRequest } from './types'

export const chatKeys = {
  options: ['chat', 'options'] as const,
  connectionModels: (connectionId: string) => ['chat', 'connection-models', connectionId] as const,
}

export function useChatOptions() {
  return useQuery({
    queryKey: chatKeys.options,
    queryFn: () => api.get<ChatOptions>('/brain/chat/options'),

    // A good answer is worth keeping: the model list comes from the provider
    // and changes only when they retire something, which is rare — but not
    // never, and this codebase has been bitten by a model disappearing twice.
    //
    // A *bad* answer is worth nothing. Caching "no models" for five minutes is
    // how a tab opened while the API was still starting stays empty long after
    // the API is fine — the page had no way to find out it was wrong, and the
    // only cure was a reload nobody knew to do.
    staleTime: (query) => (query.state.data?.models_configured ? 5 * 60_000 : 0),

    // And it re-asks on its own while there is nothing to show, so the page
    // heals when the backend comes up rather than waiting to be reloaded.
    refetchInterval: (query) => (query.state.data?.models_configured ? false : 15_000),
    refetchOnWindowFocus: true,
  })
}

/**
 * The models each Groq connection's own key can call.
 *
 * One query per connection, all at once: the picker lists every source's
 * models under its own heading, and a heading that fills in a beat after the
 * menu opens is one people miss. There are rarely more than a couple.
 */
export function useConnectionModels(connectionIds: string[]) {
  return useQueries({
    queries: connectionIds.map((id) => ({
      queryKey: chatKeys.connectionModels(id),
      queryFn: () => api.get<ChatModels>('/brain/chat/models', { connection_id: id }),
      staleTime: 5 * 60_000,
    })),
  })
}

export function useSendChat() {
  return useMutation({
    mutationFn: (body: ChatRequest) => api.post<ChatAnswer>('/brain/chat', body),
  })
}

/** One file in, its text out. Nothing is kept on the server. */
export function useReadFile() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData()
      form.append('file', file, file.name)
      return api.upload<ChatAttachment>('/brain/chat/files', form)
    },
  })
}
